import React, { useEffect, useMemo, useState } from 'react';
import { Camera, User } from 'lucide-react';
import { useUserSettingsContext } from '../../context/UserSettingsContext';
import { getUserSettings, updateUserProfile } from '../../utils/api';
import ProfileEditorModal from '../ProfileEditorModal';

const AccountSettings = () => {
  const { userSettings, updateSettings } = useUserSettingsContext();
  const [profileSettings, setProfileSettings] = useState(null);
  const [showProfileEditor, setShowProfileEditor] = useState(false);
  const settingsForDisplay = profileSettings || userSettings;

  useEffect(() => {
    if (!userSettings?.email) return;
    let cancelled = false;
    getUserSettings(userSettings.email)
      .then((fullSettings) => {
        if (!cancelled) setProfileSettings(fullSettings);
      })
      .catch((err) => console.warn('[profile] Failed to load full profile:', err));
    return () => {
      cancelled = true;
    };
  }, [userSettings?.email]);

  const displayName = useMemo(() => (
    `${settingsForDisplay?.firstName || ''} ${settingsForDisplay?.lastName || ''}`.trim() ||
    settingsForDisplay?.name ||
    settingsForDisplay?.email ||
    'User'
  ), [settingsForDisplay]);

  const initials = useMemo(() => (
    displayName
      .split(/\s+/)
      .map(part => part[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || 'U'
  ), [displayName]);

  const handleProfileSave = async (profileFields) => {
    const saved = await updateUserProfile(profileFields);
    setProfileSettings(saved);
    updateSettings?.(saved);

    const chatClient = window.streamChatClient;
    const streamUserId = (saved.email || '').toLowerCase().replace(/\./g, '_');
    if (chatClient && streamUserId) {
      try {
        await chatClient.partialUpdateUser({
          id: streamUserId,
          set: {
            name: `${saved.firstName || ''} ${saved.lastName || ''}`.trim() || saved.email,
            image: saved.profilePictureThumb || '',
          },
        });
      } catch (err) {
        console.warn('[profile] Failed to update Stream user profile:', err);
      }
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 style={{ fontFamily: 'var(--font-sans)', fontSize: 24, fontWeight: 700, color: 'var(--lodge-onyx-900)' }}>
          Account
        </h1>
        <p style={{ fontSize: 14, color: 'var(--lodge-onyx-500)', marginTop: 4 }}>
          Manage your name and profile photo.
        </p>
      </div>

      <div className="rounded-2xl border bg-white p-5 shadow-sm" style={{ borderColor: 'var(--lodge-onyx-100)' }}>
        <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <div className="relative h-16 w-16 overflow-hidden rounded-full bg-slate-100 ring-1 ring-slate-200">
              {settingsForDisplay?.profilePicture ? (
                <img src={settingsForDisplay.profilePicture} alt="" className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-lg font-semibold text-slate-600">
                  {initials}
                </div>
              )}
            </div>
            <div className="min-w-0">
              <div className="text-base font-semibold text-slate-900 truncate">{displayName}</div>
              <div className="text-sm text-slate-500 truncate">{settingsForDisplay?.email}</div>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setShowProfileEditor(true)}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-orange-600"
          >
            {settingsForDisplay?.profilePicture ? <Camera size={16} /> : <User size={16} />}
            Edit Profile
          </button>
        </div>
      </div>

      <ProfileEditorModal
        open={showProfileEditor}
        userSettings={settingsForDisplay}
        onClose={() => setShowProfileEditor(false)}
        onSave={handleProfileSave}
      />
    </div>
  );
};

export default AccountSettings;
